import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '../../../lib/utils';

const badgeVariants = cva(
  'inline-flex items-center justify-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:border-focus [&_svg]:size-3.5 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground',
        secondary: 'bg-secondary text-secondary-foreground',
        destructive: 'bg-badge-error text-white',
        outline: 'border border-border text-foreground',
        // DS component/badges — solid semantic pills. On-badge text is fixed
        // (white on dark fills, black on light fills) per the DS text/OnBadge
        // tokens, so it does not theme-flip.
        success: 'bg-badge-success text-black',
        warning: 'bg-badge-warning text-black',
        info: 'bg-badge-info text-white',
        muted: 'bg-badge-neutral text-white',
        danger: 'bg-badge-error text-white',
        // DS "secondary" desaturated status pills for the nav (New!/Beta/Soon).
        // On-badge text fixed per the DS OnBadge tokens; single value both themes.
        new: 'bg-badge-secondary-fuchsia text-white',
        beta: 'bg-badge-secondary-indigo text-white',
        soon: 'bg-badge-secondary-sky text-black',
      },
    },
    defaultVariants: { variant: 'default' },
  },
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof badgeVariants> {}

export function Badge({ className, variant, ...props }: BadgeProps) {
  return <div className={cn(badgeVariants({ variant }), className)} {...props} />;
}

export { badgeVariants };
